import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBJECTS, LEVELS, subjectStyle, SUBJECT_DOT } from "@/lib/constants";
import { Markdown } from "@/components/Markdown";
import { X, Lock, Eye, EyeOff, CheckCircle2, Crown, PencilRuler, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";

const DIFF_STYLE = {
  "Facile": "bg-emerald-100 text-emerald-700",
  "Moyen": "bg-amber-100 text-amber-700",
  "Difficile": "bg-rose-100 text-rose-700",
};

export default function Exercises() {
  const { user } = useAuth();
  const isPremium = user?.plan === "premium";
  const [exercises, setExercises] = useState([]);
  const [filters, setFilters] = useState({ subject: "all", level: user?.level || "all", theme: "all" });
  const [active, setActive] = useState(null);
  const [showCorrection, setShowCorrection] = useState(false);
  const [completeForm, setCompleteForm] = useState({ score: 100, minutes: 10 });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ subject: "Mathématiques", level: user?.level || "Seconde", theme: "" });
  const [aiLoading, setAiLoading] = useState(false);
  const [unlockIn, setUnlockIn] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (unlockIn <= 0) return;
    const t = setInterval(() => setUnlockIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [unlockIn]);

  const generateExercise = async (e) => {
    e.preventDefault();
    setAiLoading(true);
    try {
      await api.post("/ai/generate-exercise", aiForm);
      toast.success("Exercice généré par l'IA et ajouté à ta bibliothèque !");
      setAiOpen(false);
      setAiForm({ subject: "Mathématiques", level: user?.level || "Seconde", theme: "" });
      setFilters({ subject: "all", level: "all", theme: "all" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiLoading(false);
    }
  };

  const load = () => {
    const params = new URLSearchParams(filters).toString();
    api.get(`/exercises?${params}`).then(({ data }) => setExercises(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filters]);

  const themes = [...new Set(exercises.map((e) => e.theme))];

  const openExercise = (ex) => {
    if (ex.locked) return;
    setActive(ex);
    setShowCorrection(false);
    setCompleteForm({ score: 100, minutes: 10 });
    setUnlockIn(45);
    api.post(`/exercises/${ex.id}/start`).catch(() => {});
  };

  const complete = async () => {
    try {
      await api.post(`/exercises/${active.id}/complete`, { score: Number(completeForm.score), minutes: Number(completeForm.minutes) });
      toast.success("Exercice enregistré dans ta progression !");
      setActive(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="exercises-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Exercices & corrections</h1>
          <p className="text-slate-500 text-sm sm:text-base mt-1">Entraîne-toi, vérifie la correction, comprends tes erreurs.</p>
        </div>
        <button
          onClick={() => (isPremium ? setAiOpen(true) : toast.info("Passe à Premium pour générer des exercices par IA !"))}
          data-testid="ai-generate-exercise-button"
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-colors duration-200 ${
            isPremium ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-90" : "bg-slate-100 text-slate-500"
          }`}
        >
          {isPremium ? <Sparkles className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          Exercice IA
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })} data-testid="exercise-subject-filter" className="px-4 py-2.5 rounded-full border border-slate-200 text-xs font-semibold bg-white text-slate-700">
          <option value="all">Toutes les matières</option>
          {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })} data-testid="exercise-level-filter" className="px-4 py-2.5 rounded-full border border-slate-200 text-xs font-semibold bg-white text-slate-700">
          <option value="all">Tous les niveaux</option>
          {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={filters.theme} onChange={(e) => setFilters({ ...filters, theme: e.target.value })} data-testid="exercise-theme-filter" className="px-4 py-2.5 rounded-full border border-slate-200 text-xs font-semibold bg-white text-slate-700">
          <option value="all">Tous les thèmes</option>
          {themes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="animate-pulse grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-44 bg-slate-100 rounded-3xl" />)}</div>
      ) : exercises.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80" data-testid="exercises-empty">
          <PencilRuler className="h-10 w-10 text-sky-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Aucun exercice pour ces filtres.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {exercises.map((ex) => (
            <div
              key={ex.id}
              data-testid={`exercise-card-${ex.id}`}
              onClick={() => openExercise(ex)}
              className={`relative bg-white rounded-3xl border p-5 shadow-sm transition-transform duration-200 overflow-hidden ${
                ex.locked ? "border-slate-200 opacity-80" : "border-slate-200/80 hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
              }`}
            >
              <div className="h-1 w-10 rounded-full mb-3" style={{ backgroundColor: SUBJECT_DOT[ex.subject] || "#94A3B8" }} />
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${subjectStyle(ex.subject)}`}>{ex.subject}</span>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ex.level}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_STYLE[ex.difficulty] || "bg-slate-100 text-slate-600"}`}>{ex.difficulty}</span>
                {ex.tier !== "gratuit" && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${ex.tier === "premium" ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white" : "bg-indigo-100 text-indigo-700"}`}>
                    <Crown className="h-3 w-3" /> {ex.tier === "premium" ? "Premium" : "Basique"}
                  </span>
                )}
              </div>
              <h3 className="mt-3 text-base font-semibold text-slate-900">{ex.title}</h3>
              <p className="mt-1 text-xs text-slate-500">{ex.theme}</p>
              {ex.ai_generated && (
                <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-violet-600">
                  <Sparkles className="h-3 w-3" /> Généré par IA
                </span>
              )}
              {ex.completed && (
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid={`exercise-done-${ex.id}`}>
                  <CheckCircle2 className="h-4 w-4" /> Réalisé — score {ex.my_score}%
                </span>
              )}
              {ex.locked && (
                <div className="absolute inset-0 rounded-3xl bg-white/60 backdrop-blur-[1px] flex flex-col items-center justify-center gap-2" data-testid={`exercise-locked-${ex.id}`}>
                  <Lock className="h-6 w-6 text-slate-400" />
                  <Link to="/pricing" onClick={(e) => e.stopPropagation()} data-testid={`exercise-upgrade-${ex.id}`} className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline">
                    Débloquer avec {ex.tier === "premium" ? "Premium" : "Basique"}
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* AI exercise generation modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="ai-exercise-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" /> Générer un exercice par IA
              </h2>
              <button onClick={() => setAiOpen(false)} data-testid="ai-exercise-modal-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={generateExercise} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Matière</label>
                <select value={aiForm.subject} onChange={(e) => setAiForm({ ...aiForm, subject: e.target.value })} data-testid="ai-exercise-subject-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Niveau</label>
                <select value={aiForm.level} onChange={(e) => setAiForm({ ...aiForm, level: e.target.value })} data-testid="ai-exercise-level-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Thème</label>
                <input type="text" required value={aiForm.theme} onChange={(e) => setAiForm({ ...aiForm, theme: e.target.value })} data-testid="ai-exercise-theme-input" placeholder="Ex : Les équations du second degré" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <button type="submit" disabled={aiLoading} data-testid="ai-exercise-submit" className="w-full py-3 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60">
                {aiLoading ? "Génération en cours… (10-20 s)" : "Générer l'exercice"}
              </button>
              <p className="text-xs text-slate-400 text-center">L'IA crée un exercice inédit avec sa correction détaillée, adapté à ton niveau.</p>
            </form>
          </div>
        </div>
      )}

      {/* Exercise modal */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="exercise-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-7 animate-fade-in-up">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${subjectStyle(active.subject)}`}>{active.subject}</span>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{active.level} · {active.theme}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFF_STYLE[active.difficulty]}`}>{active.difficulty}</span>
              </div>
              <button onClick={() => setActive(null)} data-testid="exercise-modal-close" className="p-2 rounded-lg hover:bg-slate-100 shrink-0"><X className="h-4 w-4" /></button>
            </div>
            <h2 className="text-lg font-semibold text-slate-900" data-testid="exercise-modal-title">{active.title}</h2>
            <div
              className="mt-4 rounded-2xl border border-l-4 p-5"
              style={{
                backgroundColor: `${SUBJECT_DOT[active.subject] || "#4F46E5"}0D`,
                borderColor: `${SUBJECT_DOT[active.subject] || "#4F46E5"}2E`,
                borderLeftColor: SUBJECT_DOT[active.subject] || "#4F46E5",
              }}
            >
              <p className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: SUBJECT_DOT[active.subject] || "#4F46E5" }}>
                <FileText className="h-3.5 w-3.5" /> Énoncé
              </p>
              <div data-testid="exercise-question">
                <Markdown text={active.question} />
              </div>
            </div>
            <button
              onClick={() => setShowCorrection(!showCorrection)}
              data-testid="exercise-toggle-correction"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold hover:bg-indigo-100 transition-colors duration-200"
            >
              {showCorrection ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showCorrection ? "Masquer la correction" : "Voir la correction"}
            </button>
            {showCorrection && (
              <div className="mt-4 rounded-2xl bg-emerald-50/70 border border-emerald-200 border-l-4 border-l-emerald-500 p-5 animate-fade-in" data-testid="exercise-correction">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-3 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Correction détaillée
                </p>
                <Markdown text={active.correction} />
              </div>
            )}
            <div className="mt-6 rounded-2xl border border-slate-200 p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Enregistrer ma session</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Score (%)</label>
                  <input type="number" min={0} max={100} value={completeForm.score} onChange={(e) => setCompleteForm({ ...completeForm, score: e.target.value })} data-testid="exercise-score-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Temps passé (min)</label>
                  <input type="number" min={1} max={300} value={completeForm.minutes} onChange={(e) => setCompleteForm({ ...completeForm, minutes: e.target.value })} data-testid="exercise-minutes-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
              </div>
              <button onClick={complete} disabled={unlockIn > 0} data-testid="exercise-complete-button" className="mt-4 w-full py-3 rounded-full bg-emerald-500 text-white font-semibold text-sm hover:bg-emerald-600 transition-colors duration-200 disabled:opacity-60">
                {unlockIn > 0 ? `Réfléchis encore ${unlockIn} s…` : "Marquer comme réalisé"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
